/**
 * ToastViewport — the renderer for `lib/toast` (issue #56).
 *
 * `lib/toast` shipped as a headless queue with zero subscribers: eight
 * production modules pushed toasts and nothing put them on screen. The loser
 * of a chore-claim race (409) therefore got no feedback at all, contradicting
 * docs/ARCHITECTURE.md §"the loser must find out immediately and without
 * ambiguity … the UI shows «noen andre tok den»". This component is that
 * missing subscriber.
 *
 * Single slot, newest wins — NOT a stack. 04-UI-SPEC §"ConflictToast":
 * "If a 2nd conflict arrives while first is visible, new toast replaces (no
 * stacking — keeps the surface quiet). Auto-dismiss timer resets to 4.5s."
 * So we render only the most recent entry, restart the timer whenever a newer
 * one arrives, and clear the whole queue when it expires so nothing older
 * resurfaces behind it.
 *
 * NEVER `role="alert"` — locked as T-04-toastTone (see ConflictToast's header
 * and 02-UI-SPEC:635). Every kind, `'error'` included, is announced politely
 * via `role="status" aria-live="polite"`.
 *
 * No entrance animation (matches ConflictToast; reduced-motion-correct by
 * construction) and no close button (04-UI-SPEC:330 — "No close button — this
 * is informational, not actionable").
 *
 * The live region is ALWAYS MOUNTED and only its text content swaps. If the
 * `role="status"` container entered the DOM in the same commit as its text,
 * screen readers would commonly skip that first announcement — which is
 * exactly the "immediately and without ambiguity" the 409 claim-race loser is
 * owed. So the empty state is a live region with no text, no visible surface
 * (no border/background/padding → zero height) and `pointer-events-none`, so
 * this `fixed` z-40 box can never swallow a tap. Do NOT hide it with
 * `display:none` / `hidden` / `visibility:hidden`: all three take the region
 * out of the accessibility tree and reintroduce the bug this avoids.
 *
 * `data-testid` / `data-kind` are present ONLY while a toast is showing. The
 * handle is `entry.testId ?? 'toast'`, so an entry that carries its own
 * `testId` REPLACES the generic one rather than adding to it.
 */
import { useEffect, useState } from 'react';
import { clearToasts, subscribeToasts, type ToastEntry } from '../../lib/toast';

const AUTO_DISMISS_MS = 4_500;

/**
 * Where the live region sits — applied whether or not a toast is showing.
 *
 * Canonical statement of the toast placement rule; ConflictToast carries the
 * identical string and points here. The phone NavShell is
 * `fixed bottom-0 h-16 z-30`, so a plain `bottom-4` parks a z-40 toast on top
 * of the nav and swallows taps. #153: hovedmenyen er en pille 28 px over
 * bunnen og 64 px høy, og den lime assistentknappen står nederst til høyre på
 * alle bredder, så `bottom-28` (112 px) holder toasten fri av begge med 20 px
 * luft. The rail breakpoint is RAIL_MIN_WIDTH = 768, so `md:` — not `lg:` —
 * is where it re-anchors to the right. `z-40` is the toast rung of the #45 stacking ladder
 * (nav 30 → toast 40 → sheets 50 → idle takeover 60). Keep the two strings in
 * sync until shopping moves onto `lib/toast` and ConflictToast retires (#64).
 */
const POSITION_CLASSES =
  'fixed bottom-28 left-4 right-4 z-40 mx-auto max-w-sm md:left-auto md:right-4 md:max-w-sm';

/**
 * The visible chrome, applied only when a toast is showing. `toast-*` tokens
 * are app-global aliases of the shopping conflict surface (see theme.css) —
 * a global component must not depend on a shopping-namespaced token.
 */
const SURFACE_CLASSES =
  'rounded-lg border border-toast-border bg-toast-bg text-toast-fg shadow-md p-4 text-sm';

export function ToastViewport() {
  // `useState` + `useEffect` rather than `useSyncExternalStore`: the store
  // hands out a stable snapshot now, but uSES would additionally need a public
  // `getSnapshot` export that nothing else wants. `subscribeToasts` already
  // pushes the current queue synchronously on subscribe, so this is equivalent.
  const [entries, setEntries] = useState<readonly ToastEntry[]>([]);

  useEffect(() => subscribeToasts(setEntries), []);

  const current = entries[entries.length - 1];
  const currentId = current?.id;

  // Only a NEW toast (a new `currentId`) re-arms the countdown — putting
  // `entries` in the deps would reset 4.5s on every unrelated queue change.
  useEffect(() => {
    if (currentId === undefined) return undefined;
    // Clear the surface outright: dismissing only the visible entry would
    // resurface an older queued one that the user already missed.
    const handle = window.setTimeout(clearToasts, AUTO_DISMISS_MS);
    return () => window.clearTimeout(handle);
  }, [currentId]);

  return (
    <div
      data-testid={current ? (current.testId ?? 'toast') : undefined}
      data-kind={current?.kind}
      role="status"
      aria-live="polite"
      className={[
        POSITION_CLASSES,
        current ? SURFACE_CLASSES : 'pointer-events-none',
      ].join(' ')}
    >
      {current?.body ?? ''}
    </div>
  );
}
